import { LazyImage } from '@/shared/ui/LazyImage';
import { Portal } from '@/shared/ui/Portal';
import { SliderNavigationButton } from '@/shared/ui/SliderNavigationButton';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import PopupGallery from '@/widgets/PopupGallery/ui/PopupGallery';
import { cva } from 'class-variance-authority';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { useMedia } from 'react-use';
import { EffectFade, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';
import EmptyIcon from '~/icons/product_empty_light.svg';

interface Props {
  className?: string;
  images: string[];
}

export const Gallery: FC<Props> = ({ className, images }) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  const [initialPopupGalleryIndex, setInitialPopupGalleryIndex] = useState(0);

  const [showPopupGallery, setShowPopupGallery] = useState(false);

  // Использование хука useMedia для определения мобильной версии
  const isMobile = useMedia('(max-width: 980px)', true);

  const onClickSlide = (index: number) => {
    setInitialPopupGalleryIndex(index);
    setShowPopupGallery(true);
  };

  const [activeIndex, setActiveIndex] = useState(0); // состояние для текущего индекса слайда

  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

  const onSlide = (index: number) => {
    swiperInstance?.slideToLoop(index);
  };

  if (isMobile) {
    return (
      <div className="mt-3.7 mx-6 mb-2.4 flex flex-col gap-6.7 items-center">
        <Swiper
          direction="horizontal"
          slidesPerView={1}
          spaceBetween={10}
          loop={true}
          centeredSlides={true}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)} // обновляем индекс при смене слайда
          className="w-full"
          onSwiper={setSwiperInstance}
        >
          {images.map((image, index) => (
            <SwiperSlide key={index} className="w-full h-[24rem]">
              <LazyImage
                src={image}
                alt=""
                fill
                className="object-center object-contain mix-blend-multiply"
                wrapClassName="bg-cLightGrey"
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="flex justify-center space-x-2 mt-2">
          {images.map((_, index) => (
            <span
              key={index}
              onClick={() => onSlide(index)}
              className={`h-0.6 w-0.6 rounded-full ${index === activeIndex ? 'bg-black' : 'bg-gray-300'}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={twMergeExt(cvaRoot(), className)}>
      <div className={twMergeExt(cvaThumbsContainer(), 'mb-9')}>
        {images.length && images.length > 2 ? (
          <SliderNavigationButton
            className={cvaSliderNavigationButton({
              direction: 'prev',
            })}
            direction={'prev'}
            swiper={swiper}
            color={'outlinedOnGrey'}
          />
        ) : null}
        <Swiper
          className={cvaThumbs()}
          direction={isMobile ? 'horizontal' : 'vertical'}
          slidesPerView={'auto'}
          spaceBetween={8}
          modules={[Thumbs]}
          thumbs={{
            slideThumbActiveClass: 'swiper-slide-thumb-active',
          }}
          watchSlidesProgress
          onSwiper={setThumbsSwiper}
        >
          {images.map((i, index) => (
            <SwiperSlide key={index} className={cvaThumb()}>
              <LazyImage className={'p-0.8 object-center object-contain'} src={i} alt={''} fill />
            </SwiperSlide>
          ))}
        </Swiper>
        {images.length && images.length > 2 ? (
          <SliderNavigationButton
            className={cvaSliderNavigationButton({
              direction: 'next',
            })}
            direction={'next'}
            swiper={swiper}
            color={'outlinedOnGrey'}
          />
        ) : null}
      </div>

      <Swiper
        className={cvaSlider()}
        modules={[Thumbs, EffectFade]}
        slidesPerView={1}
        spaceBetween={12}
        touchEventsTarget={'container'}
        thumbs={{ swiper: thumbsSwiper }}
        effect={'fade'}
        fadeEffect={{ crossFade: true }}
        onSwiper={setSwiper}
      >
        {images.length ? (
          images.map((i, index) => (
            <SwiperSlide key={index} className={cvaSlide()} onClick={() => onClickSlide(index)}>
              <LazyImage
                className={'object-center object-contain mix-blend-multiply'}
                wrapClassName="my-3.2 md:my-0 bg-cLightGrey"
                src={i}
                alt={''}
                fill
              />
            </SwiperSlide>
          ))
        ) : (
          <SwiperSlide className={cvaSlide()}>
            <EmptyIcon />
          </SwiperSlide>
        )}
      </Swiper>
      <AnimatePresence>
        {showPopupGallery && (
          <Portal>
            <PopupGallery
              initialIndex={initialPopupGalleryIndex}
              items={images.map((i) => ({
                thumb: i,
                full: i,
              }))}
              onClose={() => setShowPopupGallery(false)}
            />
          </Portal>
        )}
      </AnimatePresence>
    </div>
  );
};

const cvaRoot = cva(['Gallery-cvaRoot', 'relative', 'flex flex-col-reverse md:flex-row items-center']);

const cvaThumbsContainer = cva([
  'Gallery-cvaThumbsContainer',
  'md:flex flex-col gap-1.2 hidden items-center',
  'ml-4.2 mb-8.5 lg:ml-5.6',
]);

const cvaThumbs = cva(['Gallery-cvaThumbs', 'max-h-[23.2rem] h-fit']);

const cvaThumb = cva([
  'Gallery-cvaThumb',
  'relative',
  'w-7.2 h-7.2',
  'border border-transparent rounded-[1.6rem]',
  'transition-colors duration-300',
  'cursor-pointer',
  'bg-cWhite',
  '[&.swiper-slide-thumb-active]:border-cMediumGreen hover:border-cMediumGreen',
]);

const cvaSlider = cva([
  'Gallery-cvaSlider',
  'md:pt-8.3 md:pb-[11.2rem] pl-0 sm:pl-6 pr-0 sm:pr-8 lg:pl-7.1 lg:pr-[18.8rem] ',
]);

const cvaSlide = cva(['Gallery-cvaSlide', 'relative', '!w-[42.6rem] h-[42.6rem]', 'cursor-pointer select-none']);

const cvaSliderNavigationButton = cva(
  ['Gallery-cvaSliderNavigationButton', 'w-4.8 h-4.8', 'mx-auto', 'bg-cLightGrey', 'self-center'],
  {
    variants: {
      direction: {
        prev: 'rotate-90',
        next: 'rotate-90',
      },
    },
  }
);
